<script setup lang="ts">
import { computed, onMounted, ref, watchEffect } from "vue";
import { storeToRefs } from "pinia";
import useStore from "../store.ts";
import { effectTimeToMs, setHideFlag, updateHidden } from "../utils/ElementUtils.ts";
import { UpdateOrigin, updateContextRegistry } from "../utils/UpdateContextRegistry.ts";
import { EffectType, type Component, type EffectDirection, type RenderEffect } from "../types.ts";
import type { SwitchVariant } from "../switching/types";
import ComponentWrapper from "./ComponentWrapper.vue";
import ElementRenderer from "./ElementRenderer.vue";

const props = defineProps<{ item: Component }>();

const store = useStore();
const { currentDate } = storeToRefs(store);

const containerId = props.item.props.id;

// see docs/EXTRACTED_COMMENTS.md#c024

// Committed, template-facing transition state.
const currentId = ref<string | null>(null);
const previousId = ref<string | null>(null);
const currentState = ref<"enter" | "active">("active");

// see docs/EXTRACTED_COMMENTS.md#c806
const enterVariant = ref<SwitchVariant>("crossfade");
const exitVariant = ref<SwitchVariant>("none");
const enterDirection = ref<EffectDirection | undefined>(undefined);
const exitDirection = ref<EffectDirection | undefined>(undefined);
const switchInMs = ref(0);
const switchOutMs = ref(0);
const switchEase = ref("linear");

// see docs/EXTRACTED_COMMENTS.md#c025
let enterPending = false; // current layer painted "enter", awaiting promotion
let enterCommittedEpoch = 0; // epoch the "enter" was committed at (gate for promotion)
let committedSlotStart = 0; // slot boundary epoch of the active transition
let committedWindowMs = 0; // crossfade overlap window for the active transition

const currentItem = computed(() => {
  const id = currentId.value;
  if (!id) return null;
  return (props.item.children ?? []).find((c) => c.props.id === id) ?? null;
});

onMounted(() => {
  if (props.item.alternating_props && props.item.alternating_props.continuously == null) {
    props.item.alternating_props.continuously = true;
  }
  updateHidden(containerId);
});

const intervalMs = computed(() => {
  const secRaw = Number(props.item.alternating_props?.alternating_time ?? 1);
  const sec = Number.isFinite(secRaw) && secRaw > 0 ? secRaw : 1;
  if (sec > 50) return sec;
  return Math.max(1, Math.floor(sec * 1000));
});

const eligible = computed(() => {
  const children = props.item.children ?? [];
  return children.filter((c) => !c.published.hide_flags.rule && !c.published.hide_flags.data && !c.published.hide_flags.image && !c.published.hide_flags.playlist && !c.published.hide_flags.countdown && !c.published.hide_flags.children);
});

function sumDurationMs(effects: RenderEffect[] | undefined): number {
  if (!effects || effects.length === 0) return 0;
  let total = 0;
  for (const eff of effects) total += effectTimeToMs(eff.effect_time);
  return total;
}

// see docs/EXTRACTED_COMMENTS.md#c026
function representativeEffects(list: Component[], key: "focus_change_in" | "focus_change_out"): RenderEffect[] {
  const container = props.item.effects?.[key];
  if (container && container.length) return container;
  for (const c of list) {
    const own = c.effects?.[key];
    if (own && own.length) return own;
  }
  return [];
}

// see docs/EXTRACTED_COMMENTS.md#c806
function layerVariant(effects: RenderEffect[] | undefined): SwitchVariant | null {
  switch (effects?.[0]?.effect_type) {
    case EffectType.CROSS_FADE:
      return "crossfade";
    case EffectType.FLY:
      return "fly";
    case EffectType.TURN:
      return "turn";
    case EffectType.CURTAIN:
      return "curtain";
    default:
      return null; // IN / OUT placeholders, absent list: this layer does not animate
  }
}

// see docs/EXTRACTED_COMMENTS.md#c027
function dataSwitchFor(childId: string): "enter" | "active" | "exit" {
  if (childId === currentId.value) return currentState.value;
  return "exit";
}

function dataVariantFor(childId: string): SwitchVariant {
  if (childId === currentId.value) return enterVariant.value;
  if (childId === previousId.value) return exitVariant.value;
  // Resting children keep the exit variant so finalize changes nothing (#c027);
  // "none" is visible, so they rest as a plain fade instead.
  return exitVariant.value === "none" ? "crossfade" : exitVariant.value;
}

function dataDirectionFor(childId: string): EffectDirection | undefined {
  if (childId === currentId.value) return enterDirection.value;
  // Resting children keep the exit direction too: a directionless fly/turn/curtain
  // falls back to its default offset and would slide back across the box (#c027).
  return exitDirection.value;
}

// see docs/EXTRACTED_COMMENTS.md#c028
function commit(body: () => void) {
  const handle = updateContextRegistry.create(UpdateOrigin.Switch);
  try {
    body();
  } finally {
    updateContextRegistry.seal(handle);
  }
}

watchEffect(() => {
  const alternating = props.item.alternating_props;
  const children = props.item.children ?? [];
  const list = eligible.value;
  const epochMs = currentDate.value; // heartbeat dependency

  // --- Disabled / nothing eligible: hide everything, reset. ---------------
  if (!alternating || list.length === 0) {
    const needsClear =
      currentId.value !== null ||
      previousId.value !== null ||
      children.some((c) => c.hide_flags.alternating);
    if (needsClear) {
      commit(() => {
        for (const child of props.item.children ?? []) {
          // see docs/EXTRACTED_COMMENTS.md#c029
          setHideFlag(child.props.id, "alternating", false, "alternating_relinquish", true);
        }
        currentId.value = null;
        previousId.value = null;
        currentState.value = "active";
        enterPending = false;
        committedWindowMs = 0;
        updateHidden(containerId);
      });
    }
    return;
  }

  const n = list.length;
  const ms = intervalMs.value;
  const slot = Math.floor(epochMs / ms);
  const slotIndex = ((slot % n) + n) % n;
  const slotStartEpoch = slot * ms;
  const nextId = list[slotIndex].props.id;

  // see docs/EXTRACTED_COMMENTS.md#c030
  if (nextId !== currentId.value) {
    commit(() => {
      const liveList = eligible.value;
      const current = liveList.find((c) => c.props.id === nextId);
      if (!current) return; // no longer eligible; a later heartbeat re-derives

      const prevId = currentId.value; // the child actually on screen right now
      const prevComp = prevId ? (props.item.children ?? []).find((c) => c.props.id === prevId) : null;

      // see docs/EXTRACTED_COMMENTS.md#c031
      const inEffects = current.effects?.focus_change_in ?? representativeEffects(liveList, "focus_change_in");
      const outEffects = prevComp?.effects?.focus_change_out ?? representativeEffects(liveList, "focus_change_out");
      const enterV = layerVariant(inEffects);
      const exitV = layerVariant(outEffects);
      const inTotalMs = enterV ? Math.min(ms, sumDurationMs(inEffects)) : 0;
      const outTotalMs = exitV ? Math.min(ms, sumDurationMs(outEffects)) : 0;
      const windowMs = Math.max(0, Math.min(ms, Math.max(inTotalMs, outTotalMs)));
      const intoSlot = currentDate.value - slotStartEpoch;
      const crossfade = !!prevId && prevId !== nextId && windowMs > 0 && intoSlot < windowMs;

      const visible = new Set<string>([nextId]);
      if (crossfade && prevId) visible.add(prevId);
      // see docs/EXTRACTED_COMMENTS.md#c032
      const eligibleIds = new Set(liveList.map((c) => c.props.id));
      for (const child of props.item.children ?? []) {
        const id = child.props.id;
        const hidden = eligibleIds.has(id) ? !visible.has(id) : false;
        setHideFlag(id, "alternating", hidden, "alternating_switch", true);
      }

      currentId.value = nextId;
      enterVariant.value = enterV ?? "crossfade";
      exitVariant.value = exitV ?? "none";
      enterDirection.value = inEffects[0]?.direction;
      exitDirection.value = outEffects[0]?.direction;
      committedSlotStart = slotStartEpoch;
      if (crossfade) {
        previousId.value = prevId;
        switchInMs.value = inTotalMs;
        switchOutMs.value = outTotalMs;
        committedWindowMs = windowMs;
        // see docs/EXTRACTED_COMMENTS.md#c033
        currentState.value = inTotalMs > 0 ? "enter" : "active";
        enterPending = inTotalMs > 0;
        enterCommittedEpoch = currentDate.value;
      } else {
        // Snap (no prev, identical child, no effects, or late entry).
        previousId.value = null;
        switchInMs.value = 0;
        switchOutMs.value = 0;
        committedWindowMs = 0;
        currentState.value = "active";
        enterPending = false;
      }
      updateHidden(containerId);
    });
    return;
  }

  // see docs/EXTRACTED_COMMENTS.md#c034
  if (enterPending && epochMs > enterCommittedEpoch) {
    enterPending = false;
    currentState.value = "active";
  }

  // see docs/EXTRACTED_COMMENTS.md#c035
  if (previousId.value && epochMs - committedSlotStart >= committedWindowMs) {
    const prevToHide = previousId.value;
    commit(() => {
      setHideFlag(prevToHide, "alternating", true, "alternating_finalize_hide_prev", true);
      if (previousId.value === prevToHide) {
        previousId.value = null;
      }
      updateHidden(containerId);
    });
  }
});
</script>

<template>
  <div
    v-bind="props.item.props"
    class="lumids-switch-container lumids-switch-container--alternating"
    :style="{
      '--lumids-switch-w': props.item.props.style?.width ?? '100%',
      '--lumids-switch-h': props.item.props.style?.height ?? '100%',
      '--lumids-switch-in-ms': `${Math.max(0, switchInMs)}ms`,
      '--lumids-switch-out-ms': `${Math.max(0, switchOutMs)}ms`,
      '--lumids-switch-ease': switchEase,
    }"
  >
    <ComponentWrapper
      v-for="child in props.item.children"
      :key="child.props.key"
      :item="child"
      class="lumids-switchable"
      :data-switch="dataSwitchFor(child.props.id)"
      :data-variant="dataVariantFor(child.props.id)"
      :data-direction="dataDirectionFor(child.props.id)"
    >
      <ElementRenderer :elements="child.children" />
    </ComponentWrapper>
    <div v-if="!currentItem" :id="`text-${props.item.props.id}`" v-html="props.item.published.src"></div>
  </div>
</template>
